'use client'

import * as React from 'react'
import Image from 'next/image'
import { ClientOnly } from 'components/client-only'
import { RichTextEditor } from '@repo/web-ui/components/rich-text-editor'
import {
  IconAddPhotoAlternate,
  IconArrowDownward,
  IconArrowUpward,
  IconClose,
  IconDelete,
  IconError,
  IconFormatBold,
  IconFormatItalic,
  IconFormatUnderlined,
  IconInfoBorder,
  IconNoteAdd,
} from '@repo/design-system-cf/components/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import { Typography } from '@repo/design-system-cf/components/typography'
import { useProjectDraft } from '../project-draft-context'

const MAX_STEPS = 20

export function InstructionSection() {
  const { steps, setSteps, setActiveInstructionMedia, hasValidated, setHasValidated } =
    useProjectDraft()
  const [showFillHint, setShowFillHint] = React.useState(false)

  const updateStep = (index: number, value: string) => {
    setSteps(prev => {
      const next = [...prev]
      next[index].description = value
      next[index].isValid = undefined // user touched it → reset validation
      return next
    })
  }

  const addStep = () => {
    setHasValidated(false)

    const isFilled = (s: (typeof steps)[number]) =>
      s.description.trim().length > 0 || s.media.length > 0

    // Always allow adding the second step
    if (steps.length === 1) {
      if (steps.length >= MAX_STEPS) {
        return
      }

      setSteps(prev => [
        ...prev,
        { id: crypto.randomUUID(), description: '', media: [], isValid: undefined },
      ])

      // Show hint because step 1 may be empty
      const allFilledAfterAdd = [steps[0]].every(isFilled)
      if (!allFilledAfterAdd) {
        setShowFillHint(true)
      }

      return
    }

    // From step 3 and beyond → require all existing steps to be filled
    if (steps.length >= 2) {
      const allAboveFilled = steps.every(isFilled)

      if (!allAboveFilled) {
        setShowFillHint(true)
        return
      }
    }

    // All filled → add next step
    if (steps.length < MAX_STEPS) {
      setSteps(prev => [
        ...prev,
        { id: crypto.randomUUID(), description: '', media: [], isValid: undefined },
      ])
    }
  }

  const moveStep = (index: number, direction: 'up' | 'down') => {
    setSteps(prev => {
      const next = [...prev]
      if (direction === 'up' && index > 0) {
        ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      } else if (direction === 'down' && index < next.length - 1) {
        ;[next[index + 1], next[index]] = [next[index], next[index + 1]]
      }
      return next
    })
  }

  const removeStep = (index: number) => {
    setSteps(prev => prev.filter((_, i) => i !== index))
  }

  const onDeleteMedia = (stepIndex: number, mediaIndex: number) => {
    setSteps(prev =>
      prev.map((step, i) =>
        // oxlint-disable-next-line no-negated-condition
        i !== stepIndex
          ? step
          : { ...step, media: step.media.filter((_, idx) => idx !== mediaIndex) },
      ),
    )
  }

  React.useEffect(() => {
    if (steps.length === 0) {
      setSteps([{ id: crypto.randomUUID(), description: '', media: [], isValid: undefined }])
    }
  }, [steps, setSteps])

  // Reset validation when returning to a single step
  React.useEffect(() => {
    if (steps.length === 1) {
      setHasValidated(false)
      setSteps(prev => [{ ...prev[0], isValid: undefined }])
    }
  }, [steps.length, setHasValidated, setSteps])

  React.useEffect(() => {
    const isFilled = (s: (typeof steps)[number]) =>
      s.description.trim().length > 0 || s.media.length > 0

    // With only 1 step, never show the hint (button should always be available)
    if (steps.length === 1) {
      if (showFillHint) {
        setShowFillHint(false)
      }
      return
    }

    const allFilled = steps.every(isFilled)

    if (steps.length >= 3) {
      // From step 3 onward: if not all filled, show hint instead of the add button
      if (!allFilled && !showFillHint) {
        setShowFillHint(true)
      } else if (allFilled && showFillHint) {
        setShowFillHint(false)
      }
      return
    }

    // steps.length === 2 → keep existing behavior:
    // only auto-hide the hint when both steps are filled
    if (allFilled && showFillHint) {
      setShowFillHint(false)
    }
  }, [steps, showFillHint])

  return (
    <div className="mt-5 flex flex-col tablet:flex-row justify-start w-full">
      <p className="text-12 text-cf-grey-800 font-bold flex-shrink-0 tablet:w-24 w-full text-left">
        Instructions
      </p>

      <div className="flex flex-col mt-2 tablet:mt-0 w-full gap-4">
        {steps.map((step, index) => {
          const showError = hasValidated && step.isValid === false

          return (
            <div key={step.id} className="w-full">
              <p className="text-12 text-cf-grey-1000 pb-1">Step {index + 1}</p>

              <ClientOnly>
                <RichTextEditor
                  placeholder="Describe the step"
                  autoFocus={false}
                  placeholderClassName="top-[52px]"
                  className={tw(
                    'min-h-[136px]',
                    showError &&
                      'border border-cf-red-900 rounded-[8px] hover:border-cf-transparent-grey-1000-8',
                  )}
                  onChange={val => updateStep(index, val)}
                  initialValue={step.description}
                  hideLists={true}
                  hideEmoji={false}
                  characterLimit={5000}
                  customToolbarClassName="h-[42px]"
                  customToolbarRight={
                    <div className={tw('items-center gap-3', steps.length > 1 ? 'flex' : 'hidden')}>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="tertiary"
                          size="small"
                          aria-label="Move step up"
                          iconLeft={<IconArrowUpward />}
                          onClick={() => moveStep(index, 'up')}
                          disabled={index === 0}
                          type="button"
                        />
                        <Button
                          variant="tertiary"
                          size="small"
                          aria-label="Move step down"
                          iconLeft={<IconArrowDownward />}
                          disabled={index === steps.length - 1}
                          onClick={() => moveStep(index, 'down')}
                          type="button"
                        />
                      </div>

                      <Button
                        variant="tertiary"
                        aria-label="Remove step"
                        size="small"
                        iconLeft={<IconDelete />}
                        onClick={() => removeStep(index)}
                        type="button"
                      />
                    </div>
                  }
                  customIcons={{
                    bold: <IconFormatBold className="!size-5 text-cf-purple-900" />,
                    italic: <IconFormatItalic className="!size-5 text-cf-purple-900" />,
                    underline: <IconFormatUnderlined className="!size-5 text-cf-purple-900" />,
                  }}
                  selectPhotoArea={
                    <div className="mx-3 py-2 text-sm text-cf-grey-800 border-t border-cf-transparent-grey-1000-8">
                      {step.media.length > 0 ? (
                        <div className="pb-2">
                          <div className="text-cf-grey-600 flex gap-1 pb-2">
                            <IconInfoBorder className="size-4" />
                            <Typography variant="cf-label-02">
                              You can add up to 10 {'photos or 1 video.'}
                            </Typography>
                          </div>
                          <div className="grid gap-2.5 grid-flow-dense grid-cols-[repeat(auto-fit,minmax(75px,max-content))] sm:grid-cols-[repeat(auto-fit,minmax(96px,max-content))]">
                            {step.media.map((mediaItem, mediaIndex) => (
                              <div
                                key={mediaItem.url}
                                className="group relative mx-auto transition duration-200"
                              >
                                <div className="relative w-full h-full">
                                  <Image
                                    src={mediaItem.url}
                                    alt="uploaded"
                                    width={96}
                                    height={96}
                                    className="object-cover rounded-[12px] sm:w-[96px] sm:h-[96px] w-[75px] h-[75px]"
                                  />

                                  <Button
                                    type="button"
                                    data-testid={`post-project-dialog-delete-image-button-${index}`}
                                    variant="tertiary"
                                    onClick={() => onDeleteMedia(index, mediaIndex)}
                                    className="bg-[#E5DEF5] rounded-2 absolute top-2 right-2 flex items-center justify-center z-10 md:opacity-0 md:group-hover:opacity-100"
                                    aria-label="delete image"
                                    size="small"
                                    iconRight={<IconClose className="text-cf-purple-900" />}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <Button
                        variant="ghost"
                        iconLeft={<IconAddPhotoAlternate className="!size-4" />}
                        size="small"
                        onClick={() => setActiveInstructionMedia({ stepIndex: index })}
                        type="button"
                      >
                        Add image or video
                      </Button>
                    </div>
                  }
                />
              </ClientOnly>

              {showError && index === 0 ? (
                <div className="flex gap-1 items-start pt-2">
                  <IconError className="size-3 text-cf-red-900 mt-0.5" />
                  <Typography variant="cf-label-02" className="text-cf-red-900">
                    This step is required to post your tutorial.
                  </Typography>
                </div>
              ) : null}

              {showError && index > 0 ? (
                <div className="flex gap-1 items-start pt-2">
                  <IconError className="size-3 text-cf-red-900 mt-0.5" />
                  <div>
                    <Typography variant="cf-label-02-B" className="text-cf-red-900">
                      Content is missing for this step.
                    </Typography>
                    <Typography variant="cf-label-02" className="text-cf-red-900">
                      To proceed, please add content or remove the entire step.
                    </Typography>
                  </div>
                </div>
              ) : null}
            </div>
          )
        })}

        {showFillHint ? (
          <div className="flex gap-1 items-center">
            <IconError className="size-3 text-cf-grey-600" />
            <p className="text-cf-grey-600 text-12">Fill in all steps above to add another step.</p>
          </div>
        ) : (
          <div className="flex gap-3 items-center justify-start">
            <Button
              variant="tertiary"
              size="small"
              iconLeft={<IconNoteAdd />}
              type="button"
              onClick={addStep}
              disabled={steps.length >= MAX_STEPS}
            >
              Add a step
            </Button>

            <p>
              Max {MAX_STEPS - steps.length} {MAX_STEPS - steps.length === 1 ? 'step' : 'steps'}{' '}
              left
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
